import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import { MapContainer, ClusterMarkers } from 'features/Map'
import type { ClusterPoint } from 'features/Map'
import { tradeAreaService } from 'features/Map/services/tradeAreaService'

interface MarkerData {
	id: string
	lat: number
	lng: number
	name?: string
	address?: string
	city?: string
	state?: string
	postalCode?: string
	image?: string
}

interface BulkLoadMapProps {
	markers: MarkerData[]
	center: number[]
	zoom: number
}

const MARKER_SIZE = 40

function escapeHtml(v: string): string {
	return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function buildPopupHTML(m: MarkerData): string {
	const imgBlock = m.image
		? `<img src="${escapeHtml(m.image)}" alt="${escapeHtml(m.name || '')}" style="width:64px;height:64px;object-fit:cover;border-radius:6px;flex-shrink:0;" />`
		: `<div style="width:64px;height:64px;border-radius:6px;background:#f3f4f6;display:flex;align-items:center;justify-content:center;color:#9ca3af;font-size:22px;font-weight:600;flex-shrink:0;">${escapeHtml((m.name || '?')[0])}</div>`
	const addressParts = [m.city, m.state, m.postalCode].filter(Boolean).join(', ')
	return `
		<div style="padding:4px;min-width:200px;">
			<div style="display:flex;align-items:center;gap:10px;margin-bottom:8px;">
				${imgBlock}
				<span style="font-weight:600;font-size:14px;color:#111827;">${escapeHtml(m.name || '')}</span>
			</div>
			<div style="text-align:center;font-size:12px;color:#6b7280;line-height:1.6;">
				${m.address ? `<div>${escapeHtml(m.address)}</div>` : ''}
				${addressParts ? `<div>${escapeHtml(addressParts)}</div>` : ''}
			</div>
		</div>
	`
}

function buildLeafElement(marker: MarkerData): HTMLElement {
	if (marker.image) {
		const btn = document.createElement('button')
		btn.type = 'button'
		btn.title = marker.name || ''
		btn.style.cssText = `
			display:block;padding:0;margin:0;
			width:${MARKER_SIZE}px;height:${MARKER_SIZE}px;
			border-radius:50%;overflow:hidden;
			border:2px solid #0080FE;
			box-shadow:0 2px 6px rgba(0,0,0,0.25);
			cursor:pointer;
			transition:transform 150ms ease;
		`
		btn.innerHTML = `<img src="${escapeHtml(marker.image)}" alt="${escapeHtml(marker.name || '')}" referrerpolicy="no-referrer" style="width:100%;height:100%;object-fit:cover;" />`
		btn.addEventListener('mouseenter', () => { btn.style.transform = 'scale(1.1)' })
		btn.addEventListener('mouseleave', () => { btn.style.transform = 'scale(1)' })
		return btn
	}
	const el = document.createElement('div')
	el.style.cssText = `
		width:18px;height:18px;border-radius:50%;
		background:#0080FE;border:2px solid #ffffff;
		box-shadow:0 2px 4px rgba(0,0,0,0.3);
		cursor:pointer;transition:transform 150ms ease;
	`
	el.addEventListener('mouseenter', () => { el.style.transform = 'scale(1.2)' })
	el.addEventListener('mouseleave', () => { el.style.transform = 'scale(1)' })
	return el
}

const BulkLoadMap = ({ markers, center, zoom }: BulkLoadMapProps) => {
	const [map, setMap] = useState<maplibregl.Map | null>(null)
	const popupRef = useRef<maplibregl.Popup | null>(null)
	const didInitRef = useRef(false)

	const points = useMemo<ClusterPoint[]>(
		() =>
			markers
				.filter((m) => m.lat != null && m.lng != null)
				.map((m) => ({
					id: m.id,
					longitude: m.lng,
					latitude: m.lat,
					properties: { name: m.name, address: m.address, city: m.city, state: m.state, postalCode: m.postalCode, image: m.image },
				})),
		[markers],
	)

	const handleMapLoad = useCallback((m: maplibregl.Map) => {
		setMap(m)
	}, [])

	useEffect(() => {
		if (!map || didInitRef.current) return
		if (markers.length > 1) {
			const bounds = tradeAreaService.getBoundsForSites(
				markers.map((m) => ({ longitude: m.lng, latitude: m.lat })),
			)
			map.fitBounds(bounds, { padding: 60, maxZoom: 15, animate: false })
		} else if (markers.length === 1) {
			map.setCenter([markers[0].lng, markers[0].lat])
			map.setZoom(zoom)
		} else {
			map.setCenter([center[1], center[0]])
			map.setZoom(zoom)
		}
		didInitRef.current = true
	}, [map, markers, center, zoom])

	const handleLeafClick = useCallback(
		(pointId: string) => {
			if (!map) return
			const marker = markers.find((m) => m.id === pointId)
			if (!marker) return
			popupRef.current?.remove()
			const popup = new maplibregl.Popup({ offset: 24, closeButton: true })
				.setLngLat([marker.lng, marker.lat])
				.setHTML(buildPopupHTML(marker))
				.addTo(map)
			popupRef.current = popup
		},
		[map, markers],
	)

	const renderLeaf = useCallback(
		(point: ClusterPoint, onClick: () => void): HTMLElement => {
			const props = point.properties as Partial<MarkerData> | undefined
			const markerData: MarkerData = {
				id: point.id,
				lat: point.latitude,
				lng: point.longitude,
				name: props?.name,
				image: props?.image,
			}
			const el = buildLeafElement(markerData)
			el.addEventListener('click', (e) => {
				e.stopPropagation()
				onClick()
			})
			return el
		},
		[],
	)

	useEffect(() => {
		if (!map) return
		const dismiss = () => popupRef.current?.remove()
		map.on('click', dismiss)
		return () => { map.off('click', dismiss) }
	}, [map])

	return (
		<div className="shadow-xl rounded-md overflow-hidden h-[680px]">
			<MapContainer onMapLoad={handleMapLoad}>
				<ClusterMarkers
					map={map}
					isMapLoaded={Boolean(map)}
					points={points}
					renderLeaf={renderLeaf}
					onLeafClick={handleLeafClick}
				/>
			</MapContainer>
		</div>
	)
}

export default BulkLoadMap
